import { useEffect, useMemo, useState } from 'react';
import StripeCheckout, { RefundModal } from '../components/StripeCheckout';
import { getReceipts, isRefundEligible } from '../utils/stripe';
import { useTr } from '../utils/useTr';
import { db } from '../utils/supabase';
import { useApp } from '../contexts/AppContext';
import { getEventTypes, getEventPrice, getSpotsLeft, formatEventDate } from '../utils/events';
import { canAccess } from '../utils/monetization';

// ── Calendar helpers ──────────────────────────────────────────────────────
export function toICSDate(d, t) {
  if (!d) return '';
  // Normalise time: strip seconds if already "HH:MM:SS", pad to "HH:MM"
  const time = (t || '09:00').replace(/^(\d{2}:\d{2})(:\d{2})?$/, '$1');
  const dt = new Date(`${d}T${time}:00`);
  if (isNaN(dt.getTime())) return '';
  return dt.toISOString().replace(/[-:]/g,'').replace('.000','');
}
export function generateICS(events) {
  const now = new Date();
  const nowDate = now.toISOString().split('T')[0];
  const nowTime = now.toTimeString().slice(0,5);
  const dtstamp = toICSDate(nowDate, nowTime);
  const lines = ['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//BeeLove//Events//EN','CALSCALE:GREGORIAN','METHOD:PUBLISH'];
  events.forEach(ev => {
    const dtstart = toICSDate(ev.date, ev.time);
    const dtend   = toICSDate(ev.date, ev.end_time || ev.endTime || ev.time);
    if (!dtstart) return; // skip events with no valid date
    lines.push(
      'BEGIN:VEVENT',
      `UID:${ev.id}@beelove.me`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${dtstart}`,
      `DTEND:${dtend || dtstart}`,
      `SUMMARY:${(ev.title||'').replace(/[,;\\]/g, '\\$&')}`,
      `DESCRIPTION:${(ev.description||'').replace(/\n/g,'\\n').replace(/[,;\\]/g,'\\$&')}`,
      `LOCATION:${[ev.location, ev.address].filter(Boolean).join(', ')}`,
      `URL:https://beelove.me/#events`,
      'END:VEVENT',
    );
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}
export function downloadICS(events, filename='beelove-events.ics') {
  const blob = new Blob([generateICS(events)],{type:'text/calendar;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
}
export function googleCalendarUrl(ev) {
  const s = ev.date?.replace(/-/g,'')+'T'+(ev.time||'090000').replace(':','')+'00';
  const e = ev.date?.replace(/-/g,'')+'T'+(ev.endTime||'120000').replace(':','')+'00';
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(ev.title)}&dates=${s}/${e}&details=${encodeURIComponent(ev.description||'')}&location=${encodeURIComponent((ev.location||'')+', '+(ev.address||''))}`;
}

// ── QR Code (pure SVG, no lib needed) ────────────────────────────────────
export function QRCodeSVG({ value, size = 160 }) {
  // Simple deterministic pixel pattern from string hash
  const hash = useMemo(() => {
    let h = 0;
    for (let i = 0; i < value.length; i++) { h = ((h << 5) - h) + value.charCodeAt(i); h |= 0; }
    return Math.abs(h);
  }, [value]);

  const N = 21; // 21×21 QR modules
  const cell = size / N;

  // Generate module pattern deterministically from hash
  const modules = useMemo(() => {
    const grid = [];
    let seed = hash;
    const rand = () => { seed = (seed * 1664525 + 1013904223) & 0xffffffff; return (seed >>> 0) / 0xffffffff; };
    for (let row = 0; row < N; row++) {
      grid[row] = [];
      for (let col = 0; col < N; col++) {
        if ((row < 7 && col < 7) || (row < 7 && col >= N-7) || (row >= N-7 && col < 7)) {
          const fr = row < 7 ? row : row-(N-7);
          const fc = col < 7 ? col : col-(N-7);
          if (row >= N-7) { const rr = row-(N-7); grid[row][col] = (rr===0||rr===6||fc===0||fc===6||(rr>=2&&rr<=4&&fc>=2&&fc<=4)); }
          else grid[row][col] = (fr===0||fr===6||fc===0||fc===6||(fr>=2&&fr<=4&&fc>=2&&fc<=4));
        } else {
          grid[row][col] = rand() > 0.52;
        }
      }
    }
    return grid;
  }, [hash]);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
      <rect width={size} height={size} fill="white" rx="4"/>
      {modules.map((rowData, ri) => rowData.map((on, ci) => on ? (
        <rect key={`${ri}-${ci}`} x={ci*cell} y={ri*cell} width={cell} height={cell} fill="#0A0A0A"/>
      ) : null))}
    </svg>
  );
}

// ── QR Ticket sheet ───────────────────────────────────────────────────────
export function TicketSheet({ event, currentUser, attended, onClose, tr }) {
  const qrValue = `beelove:event:${event.id}:user:${currentUser?.id||'me'}`;
  const type = getEventTypes('en')[event.type] || getEventTypes('en').apero;
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(10px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', maxWidth:420, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', padding:'1.5rem', paddingBottom:'2.5rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.2rem' }}>
          <div>
            <div style={{ fontSize:'0.58rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'0.2rem' }}>{tr('events.qr_title')}</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', fontWeight:500, color:'var(--white)' }}>{event.title}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
        </div>

        {/* QR Code */}
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:'1.2rem' }}>
          <div style={{ padding:'1rem', background:'white', borderRadius:'var(--r-md)', boxShadow:'0 4px 24px rgba(0,0,0,0.4)', marginBottom:'0.8rem' }}>
            <QRCodeSVG value={qrValue} size={160}/>
          </div>
          {attended ? (
            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'rgba(78,201,148,0.15)', border:'1px solid rgba(78,201,148,0.4)', borderRadius:'var(--r-pill)', padding:'0.35rem 0.9rem' }}>
              <span style={{ color:'#4EC994', fontSize:'0.8rem', fontWeight:700 }}>✓</span>
              <span style={{ color:'#4EC994', fontSize:'0.75rem', fontWeight:600 }}>{tr('events.qr_validated')}</span>
            </div>
          ) : (
            <div style={{ fontSize:'0.72rem', color:'var(--muted)', textAlign:'center' }}>{tr('events.qr_show')}</div>
          )}
        </div>

        {/* Event info */}
        <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.8rem 1rem', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem' }}>
          <div>
            <div style={{ fontSize:'0.58rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.15rem' }}>{tr('events.date_time')}</div>
            <div style={{ fontSize:'0.78rem', color:'var(--white)', fontWeight:500 }}>{formatEventDate(event.date)}</div>
            <div style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{event.time} – {event.endTime}</div>
          </div>
          <div>
            <div style={{ fontSize:'0.58rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.15rem' }}>{tr('events.location')}</div>
            <div style={{ fontSize:'0.78rem', color:'var(--white)', fontWeight:500 }}>{event.location}</div>
            <div style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{event.address}</div>
          </div>
        </div>

        {/* Name */}
        <div style={{ marginTop:'0.8rem', textAlign:'center', fontSize:'0.78rem', color:'var(--muted)' }}>
          {currentUser?.name} · <span style={{ color:'var(--gold)' }}>{tr('events.qr_registered')}</span>
        </div>
      </div>
    </div>
  );
}

// ── Attendees sheet ───────────────────────────────────────────────────────
export function AttendeesSheet({ event, onClose, tr }) {
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', maxWidth:420, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', maxHeight:'75vh', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'1.2rem 1.2rem 0.8rem', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', fontWeight:500, color:'var(--white)' }}>{tr('events.attendees_title')}</div>
            <div style={{ fontSize:'0.65rem', color:'var(--muted)', marginTop:'0.15rem' }}>{tr('events.attendees_sub')}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted)', cursor:'pointer', fontSize:'1.2rem' }}>✕</button>
        </div>
        <div style={{ overflowY:'auto', flex:1, padding:'1rem' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
            {(event.attendees||[]).map(att => (
              <div key={att.id} style={{ display:'flex', alignItems:'center', gap:'0.8rem', padding:'0.6rem 0.7rem', background:'var(--dark)', border:`1px solid ${att.mutual ? 'var(--gold-border)' : 'var(--border)'}`, borderRadius:'var(--r-md)' }}>
                <div style={{ width:40, height:40, borderRadius:'50%', overflow:'hidden', flexShrink:0, border:`2px solid ${att.mutual ? 'var(--gold)' : 'var(--border)'}` }}>
                  <img src={att.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:'0.85rem', fontWeight:500, color:'var(--white)' }}>{att.name}</div>
                  {att.mutual && <div style={{ fontSize:'0.65rem', color:'var(--gold)', marginTop:'0.1rem' }}>✦ {tr('matches.matched')}</div>}
                </div>
                {att.mutual && (
                  <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--gold)', flexShrink:0 }}/>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────
function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, Math.round((value/max)*100)) : 0;
  return (
    <div style={{ height:3, background:'rgba(255,255,255,0.08)', borderRadius:2, overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${pct}%`, background:color||'var(--gold)', borderRadius:2, transition:'width 0.4s' }}/>
    </div>
  );
}

// ── Event Card ────────────────────────────────────────────────────────────
function EventCard({ event, currentUser, onOpen, registered, attended, lang }) {
  const tr = useTr();
  const types = getEventTypes(lang);
  const type = types[event.type] || types.apero;
  const price = getEventPrice(event, currentUser);
  const spotsLeft = getSpotsLeft(event);
  const isFree = price === 0 || price == null;
  const isPast = event.status === 'past';

  return (
    <div onClick={() => onOpen(event)}
      style={{ background:'var(--dark)', border:`1px solid ${registered ? 'var(--gold-border)' : 'var(--border)'}`, borderRadius:'var(--r-lg)', overflow:'hidden', cursor:'pointer', transition:'transform 0.15s, box-shadow 0.15s', opacity: isPast ? 0.85 : 1 }}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.3)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}
    >
      <div style={{ height:150, position:'relative', overflow:'hidden' }}>
        <img src={(Array.isArray(event.images) && event.images[0]) || event.image || ''} alt={event.title} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block', filter: isPast ? 'grayscale(40%)' : 'none' }}/>
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6))' }}/>
        <div style={{ position:'absolute', top:10, left:10, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(8px)', border:`1px solid ${type.color}44`, padding:'0.18rem 0.55rem', borderRadius:'var(--r-pill)', display:'flex', alignItems:'center', gap:'0.3rem' }}>
          <span style={{ fontSize:'0.75rem' }}>{type.icon}</span>
          <span style={{ fontSize:'0.58rem', color:type.color, fontFamily:'Outfit', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase' }}>{type.label}</span>
        </div>
        {attended && (
          <div style={{ position:'absolute', top:10, right:10, background:'rgba(78,201,148,0.85)', padding:'0.2rem 0.6rem', borderRadius:'var(--r-pill)' }}>
            <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#0A0A0A', fontFamily:'Outfit' }}>{tr('events.attended_badge')}</span>
          </div>
        )}
        {!isPast && !attended && (
          <div style={{ position:'absolute', top:10, right:10, background: isFree ? 'rgba(78,201,148,0.85)' : 'rgba(0,0,0,0.75)', backdropFilter:'blur(8px)', padding:'0.2rem 0.6rem', borderRadius:'var(--r-pill)' }}>
            <span style={{ fontSize:'0.72rem', fontWeight:700, color: isFree ? '#0A0A0A' : 'var(--gold)', fontFamily:'Outfit' }}>{isFree ? tr('events.free') : `$${price}`}</span>
          </div>
        )}
      </div>
      <div style={{ padding:'0.85rem 1rem' }}>
        <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', fontWeight:500, color:'var(--white)', marginBottom:'0.15rem', lineHeight:1.2 }}>{event.title}</h3>
        <p style={{ fontSize:'0.68rem', color:'var(--muted)', marginBottom:'0.6rem' }}>{event.subtitle}</p>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.28rem', marginBottom:'0.6rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.7rem', color:'var(--muted)' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            {formatEventDate(event.date)} · {event.time}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.7rem', color:'var(--muted)' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {event.location}
          </div>
        </div>
        {isPast ? (
          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <span style={{ fontSize:'0.65rem', color:'var(--muted)' }}>{tr('events.capacity_label',{n:event.confirmed||event.registered})} {tr('events.attended_badge').toLowerCase()}</span>
            {(event.attendees||[]).length > 0 && attended && (
              <span style={{ fontSize:'0.65rem', color:'var(--gold)', marginLeft:'auto' }}>{tr('events.attendees_title')} →</span>
            )}
          </div>
        ) : (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.6rem' }}>
              <ProgressBar value={event.registered} max={event.capacity} color={type.color}/>
              <span style={{ fontSize:'0.6rem', color:'var(--muted)', flexShrink:0 }}>{event.registered}/{event.capacity}</span>
            </div>
            <button style={{ width:'100%', padding:'0.5rem', background: registered ? 'var(--gold-pale)' : isFree ? 'var(--gold)' : 'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', color: registered || !isFree ? 'var(--gold)' : 'var(--black)', fontFamily:'Outfit', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.06em' }}>
              {registered ? `✦${tr('events.qr_registered')}` : spotsLeft === 0 ? tr('events.waitlist') : isFree ? tr('events.reserve') : `${tr('events.reserve')} — $${price}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Event Modal ───────────────────────────────────────────────────────────
export function EventModal({ event, currentUser, onClose, onRegister, registered, attended, onShowQR, onShowAttendees, lang }) {
  const tr = useTr();
  const types = getEventTypes(lang);
  const type = types[event.type] || types.apero;
  const price = getEventPrice(event, currentUser);
  const spotsLeft = getSpotsLeft(event);
  const isFree = price === 0 || price == null;
  const isPast = event.status === 'past';
  const plan = currentUser?.plan || 'free';

  // Photo gallery — support both legacy `image` and new `images` array
  const photos = Array.isArray(event.images) && event.images.length > 0
    ? event.images
    : (event.image ? [event.image] : []);
  const [photoIdx, setPhotoIdx] = useState(0);

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:800, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', borderBottom:'none', maxWidth:480, width:'100%', maxHeight:'90vh', display:'flex', flexDirection:'column', borderRadius:'var(--r-xl) var(--r-xl) 0 0', overflow:'hidden' }}>
        <div style={{ height:200, position:'relative', flexShrink:0 }}>
          <img src={photos[photoIdx] || ''} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', filter: isPast ? 'grayscale(30%)' : 'none', transition:'opacity 0.2s' }}/>
          {photos.length > 1 && <>
            <div onClick={() => setPhotoIdx(i => (i - 1 + photos.length) % photos.length)}
              style={{ position:'absolute', left:0, top:0, width:'35%', height:'100%', cursor:'pointer' }}/>
            <div onClick={() => setPhotoIdx(i => (i + 1) % photos.length)}
              style={{ position:'absolute', right:0, top:0, width:'35%', height:'100%', cursor:'pointer' }}/>
            <div style={{ position:'absolute', top:8, left:8, right:8, display:'flex', gap:3 }}>
              {photos.map((_,i) => (
                <div key={i} onClick={() => setPhotoIdx(i)}
                  style={{ flex:1, height:2, background: i===photoIdx ? '#fff' : 'rgba(255,255,255,0.35)', borderRadius:1, cursor:'pointer' }}/>
              ))}
            </div>
          </>}
          <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.8))' }}/>
          <button onClick={onClose} style={{ position:'absolute', top:12, right:12, width:32, height:32, borderRadius:'50%', background:'rgba(0,0,0,0.5)', border:'1px solid rgba(255,255,255,0.2)', color:'var(--white)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem' }}>✕</button>
          {attended && (
            <div style={{ position:'absolute', top:12, left:12, background:'rgba(78,201,148,0.85)', padding:'0.2rem 0.7rem', borderRadius:'var(--r-pill)' }}>
              <span style={{ fontSize:'0.65rem', fontWeight:700, color:'#0A0A0A', fontFamily:'Outfit' }}>{tr('events.you_attended')}</span>
            </div>
          )}
          <div style={{ position:'absolute', bottom:14, left:16, right:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.3rem' }}>
              <span style={{ fontSize:'0.8rem' }}>{type.icon}</span>
              <span style={{ fontSize:'0.6rem', color:type.color, fontFamily:'Outfit', fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase' }}>{type.label}</span>
            </div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.5rem', fontWeight:500, color:'var(--white)', lineHeight:1.2 }}>{event.title}</h2>
          </div>
        </div>

        <div style={{ overflowY:'auto', flex:1, padding:'1.2rem 1.2rem 0' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem', marginBottom:'1rem' }}>
            {[
              { icon:'M3 4h18v18H3V4zm5-2v4M16 2v4M3 10h18', label:tr('events.date_time'), val:`${formatEventDate(event.date)}\n${event.time} – ${event.endTime}` },
              { icon:'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0zM12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', label:tr('events.location'), val:`${event.location}\n${event.address}` },
            ].map(i=>(
              <div key={i.label} style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'0.7rem 0.8rem' }}>
                <div style={{ fontSize:'0.55rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:'0.3rem' }}>{i.label}</div>
                <div style={{ fontSize:'0.76rem', color:'var(--white)', lineHeight:1.5, whiteSpace:'pre-line' }}>{i.val}</div>
              </div>
            ))}
          </div>
          {/* Price row */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.55rem 0.8rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', marginBottom:'1rem' }}>
            <span style={{ fontSize:'0.62rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{tr('events.price_label') || 'Prix / Price'}</span>
            <span style={{ fontSize:'0.88rem', fontWeight:700, color: isFree ? '#4EC994' : 'var(--gold)', fontFamily:'Outfit' }}>
              {isFree ? (lang === 'fr' ? 'Gratuit' : 'Free') : `$${price} CAD`}
            </span>
          </div>

          <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.7, marginBottom:'0.8rem' }}>{event.description}</p>

          {(event.highlights||[]).length > 0 && (
            <div style={{ marginBottom:'1rem' }}>
              {event.highlights.map((h,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.35rem' }}>
                  <div style={{ width:4, height:4, borderRadius:'50%', background:'var(--gold)', flexShrink:0 }}/>
                  <span style={{ fontSize:'0.78rem', color:'var(--muted)' }}>{h}</span>
                </div>
              ))}
            </div>
          )}

          {/* Past event: attendees preview */}
          {isPast && attended && (event.attendees||[]).length > 0 && (
            <div style={{ marginBottom:'1rem' }}>
              <div style={{ fontSize:'0.6rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'0.5rem' }}>{tr('events.attendees_title')}</div>
              <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'0.5rem' }}>
                {event.attendees.slice(0,6).map(att => (
                  <div key={att.id} style={{ width:40, height:40, borderRadius:'50%', overflow:'hidden', border:`2px solid ${att.mutual ? 'var(--gold)' : 'var(--border)'}` }}>
                    <img src={att.photo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  </div>
                ))}
                {event.attendees.length > 6 && (
                  <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--dark)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem', color:'var(--muted)' }}>+{event.attendees.length-6}</div>
                )}
              </div>
              <button onClick={onShowAttendees} style={{ background:'none', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', padding:'0.4rem 0.8rem', color:'var(--gold)', fontSize:'0.72rem', cursor:'pointer', fontFamily:'Outfit', fontWeight:600 }}>
                {tr('events.attendees_title')} →
              </button>
            </div>
          )}
        </div>

        <div style={{ padding:'1rem 1.2rem', borderTop:'1px solid var(--border)', flexShrink:0 }}>
          {isPast ? (
            <div style={{ display:'flex', gap:'0.5rem' }}>
              {attended && (
                <button onClick={onShowQR} style={{ flex:1, padding:'0.75rem', background:'var(--gold-pale)', border:'1px solid var(--gold-border)', borderRadius:'var(--r-md)', color:'var(--gold)', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:700, cursor:'pointer' }}>
                  ✦{tr('events.qr_title')}
                </button>
              )}
              {attended && (event.attendees||[]).length > 0 && (
                <button onClick={onShowAttendees} style={{ flex:1, padding:'0.75rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--white)', fontFamily:'Outfit', fontSize:'0.78rem', fontWeight:600, cursor:'pointer' }}>
                  {tr('events.attendees_title')}
                </button>
              )}
            </div>
          ) : (
            <>
              <button onClick={()=>onRegister(event)} style={{ width:'100%', padding:'0.85rem', background: registered ? 'var(--gold-pale)' : 'var(--gold)', border: registered ? '1px solid var(--gold-border)' : 'none', borderRadius:'var(--r-md)', color: registered ? 'var(--gold)' : 'var(--black)', fontFamily:'Outfit', fontSize:'0.82rem', fontWeight:700, cursor:'pointer', letterSpacing:'0.08em' }}>
                {registered ? `✦${tr('events.qr_title')} — ${tr('events.qr_registered')}` : spotsLeft===0 ? tr('events.waitlist') : isFree ? tr('events.reserve_free') : `${tr('events.reserve')} — $${price} CAD`}
              </button>
              {registered && isRefundEligible && isRefundEligible(event) && (
                <button onClick={async (e)=>{e.stopPropagation(); onShowRefund?.();}}
                  style={{ width:'100%', marginTop:'0.4rem', padding:'0.45rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.68rem', cursor:'pointer' }}>
                  {tr('payment.cancel_title')} ↩
                </button>
              )}
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem', flexWrap:'wrap' }}>
                <button onClick={()=>downloadICS([event])} style={{ flex:1, padding:'0.45rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.65rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  .ics / Apple
                </button>
                <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer" style={{ flex:1, padding:'0.45rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-md)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.65rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.3rem', textDecoration:'none' }}>
                  Google Calendar
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function isSubscribed(user) {
  const p = user?.plan;
  return p === 'honey' || p === 'royal' || p === 'founder';
}

// ── Circles teaser banner ─────────────────────────────────────────────────
// ── Main Events page ──────────────────────────────────────────────────────
export default function Events() {
  const { currentUser, features, lang, setPage, registeredEvents, setRegisteredEvents, attendedEvents, sponsors, registerForEvent, cancelEventRegistration, joinEventWaitlist, leaveEventWaitlist, waitlistEvents } = useApp();
  const tr = useTr();
  // Load events from Supabase
  const [dbEvents, setDbEvents] = useState(null);
  useEffect(() => {
    if (!db) return;
    db.events_full()
      ?.select('*, event_pricing(*)')
      .in('status', ['published', 'past'])
      .order('date', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data?.length > 0) setDbEvents(data);
      });
  }, []);

  const handleRegister = async (event) => {
    if (!currentUser) { setPage('auth'); return; }
    const spotsLeft = getSpotsLeft ? getSpotsLeft(event, registeredEvents) : (event.capacity - (event.registered_count || 0));
    // If full — join waitlist instead
    if (spotsLeft <= 0 && !registeredEvents.has(event.id)) {
      if (waitlistEvents?.has(event.id)) {
        await leaveEventWaitlist?.(event.id);
      } else {
        await joinEventWaitlist?.(event.id);
      }
      return;
    }
    const price = getEventPrice(event, currentUser);
    if (price === 0) {
      const { error } = await registerForEvent(event.id, null, 0);
      if (!error) setRegisteredEvents(prev => new Set([...prev, event.id]));
    } else {
      setOpenEvent(event);
      setShowCheckout(true);
    }
  };
  const [tab, setTab] = useState('upcoming');     // 'upcoming' | 'past'
  const [filter, setFilter] = useState('all');
  const [openEvent, setOpenEvent] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const receipts = getReceipts();
  const [showAttendees, setShowAttendees] = useState(false);

  // Registered state (local if AppContext doesn't provide it)
  const [localReg, setLocalReg] = useState(new Set());
  const regSet = registeredEvents || localReg;
  const setReg = setRegisteredEvents || setLocalReg;
  const attSet = attendedEvents || new Set(['evt_past_001','evt_past_002']);

  if (features?.events === false) {
    return (
      <div style={{ padding:'3rem 1.5rem', textAlign:'center' }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.5rem', color:'var(--gold)', marginBottom:'0.8rem', lineHeight:1 }}>✦</div>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.5rem', color:'var(--white)', marginBottom:'0.5rem' }}>{tr('events.coming_soon')}</h2>
        <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.6 }}>{tr('events.coming_sub2')}</p>
      </div>
    );
  }

  // dbEvents is loaded from Supabase (events_full view) via AppContext
  // No mock fallback — show empty state if no events published yet
  const allEvents      = (dbEvents || []).filter(e => e.status === 'published' || e.status === 'past');
  const upcomingEvents = allEvents.filter(e => e.status === 'published');
  const pastEvents     = allEvents.filter(e => e.status === 'past');
  const events = tab === 'upcoming' ? upcomingEvents : pastEvents;
  const types = ['all', ...new Set(upcomingEvents.map(e => e.type))];
  const filtered = tab === 'past' ? events : (filter === 'all' ? events : events.filter(e => e.type === filter));
  const eventTypes = getEventTypes(lang);

  return (
    <div style={{ paddingBottom:'1rem' }}>
      {/* Hero */}
      <div style={{ padding:'1.5rem 1.2rem 1rem', borderBottom:'1px solid var(--border)' }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:400, color:'var(--white)', marginBottom:'0.2rem' }}>
          {tr('events.title')} <span style={{ color:'var(--gold)', fontStyle:'italic' }}>{tr('events.city_label')}</span>
        </h1>
        <p style={{ fontSize:'0.78rem', color:'var(--muted)' }}>{tr('events.subtitle')}</p>
      </div>

      {/* Past / Upcoming tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)' }}>
        {[['upcoming', tr('events.tab_upcoming')], ['past', tr('events.tab_past')]].map(([id, label]) => (
          <button key={id} onClick={() => { setTab(id); setFilter('all'); }}
            style={{ flex:1, padding:'0.75rem', background:'none', border:'none', borderBottom:`2px solid ${tab===id ? 'var(--gold)' : 'transparent'}`, color: tab===id ? 'var(--gold)' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.75rem', fontWeight: tab===id ? 700 : 400, letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', transition:'all 0.2s' }}>
            {label}
            {id==='past' && pastEvents.length > 0 && (
              <span style={{ marginLeft:'0.4rem', fontSize:'0.6rem', background: tab==='past' ? 'var(--gold)' : 'rgba(255,255,255,0.1)', color: tab==='past' ? 'var(--black)' : 'var(--muted)', borderRadius:99, padding:'0.1rem 0.4rem', fontWeight:700 }}>{pastEvents.filter(e=>attSet.has(e.id)).length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Type filters (upcoming only) */}
      {tab === 'upcoming' && (
        <div style={{ display:'flex', gap:'0.4rem', overflowX:'auto', padding:'0.8rem 1.2rem', scrollbarWidth:'none', msOverflowStyle:'none', borderBottom:'1px solid var(--border)' }}>
          {types.map(t => {
            const type = eventTypes[t];
            const active = filter === t;
            return (
              <button key={t} onClick={() => setFilter(t)} style={{ flexShrink:0, padding:'0.3rem 0.75rem', border:`1px solid ${active ? 'var(--gold)' : 'var(--border)'}`, borderRadius:'var(--r-pill)', background: active ? 'var(--gold-pale)' : 'transparent', color: active ? 'var(--gold)' : 'var(--muted)', fontFamily:'Outfit', fontSize:'0.68rem', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:'0.3rem', whiteSpace:'nowrap' }}>
                {type ? <>{type.icon} {type.label}</> : tr('events.all_events')}
              </button>
            );
          })}
        </div>
      )}

      {/* Partners strip (upcoming only) */}
      {tab === 'upcoming' && (
        <div style={{ padding:'0.7rem 1.2rem', background:'rgba(201,168,76,0.04)', borderBottom:'1px solid var(--border)' }}>
          <p style={{ fontSize:'0.58rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.4rem' }}>{tr('events.partners_label')}</p>
          <div style={{ display:'flex', gap:'0.6rem', overflowX:'auto', scrollbarWidth:'none' }}>
            {(sponsors||[]).filter(p=>p.status==="active").map(p=>(
              <div key={p.id} style={{ flexShrink:0, display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.3rem 0.6rem', background:'var(--dark)', border:'1px solid var(--border)', borderRadius:'var(--r-md)' }}>
                <div style={{ width:24, height:24, borderRadius:'var(--r-sm)', overflow:'hidden', flexShrink:0 }}>
                  <img src={p.logo} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                </div>
                <div>
                  <div style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--white)', fontFamily:'Outfit' }}>{p.name}</div>
                  <div style={{ fontSize:'0.55rem', color:'var(--gold)' }}>{p.discount}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar export (upcoming only) */}
      {tab === 'upcoming' && filtered.length > 0 && (
        <div style={{ padding:'0.5rem 1.2rem', display:'flex', gap:'0.5rem', alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ fontSize:'0.62rem', color:'var(--muted)', marginRight:'0.2rem' }}>{tr('events.export')}</span>
          <button onClick={() => downloadICS(filtered)} style={{ display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.25rem 0.65rem', background:'transparent', border:'1px solid var(--border)', borderRadius:'var(--r-pill)', color:'var(--muted)', fontFamily:'Outfit', fontSize:'0.62rem', cursor:'pointer' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            .ics / Apple / Outlook
          </button>
        </div>
      )}

      {/* Event cards */}
      <div style={{ padding:'1rem 1.2rem', display:'flex', flexDirection:'column', gap:'1rem' }}>
        {filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'3rem 1.5rem' }}>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'2.5rem', color:'var(--gold)', marginBottom:'0.8rem', lineHeight:1 }}>✦</div>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.3rem', color:'var(--white)', marginBottom:'0.4rem' }}>
              {tab === 'past' ? (tr('events.no_past') || 'No past events') : (tr('events.no_upcoming') || 'No upcoming events')}
            </p>
            <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.6 }}>
              {tab === 'past'
                ? 'Past BeeLove events will appear here.'
                : 'The first events in your network are coming soon. Check back shortly.'}
            </p>
          </div>
        )}
        {filtered.map(event => (
          <EventCard key={event.id} event={event} currentUser={currentUser} lang={lang}
            registered={regSet.has(event.id)}
            attended={attSet.has(event.id)}
            onOpen={e => setOpenEvent(e)}/>
        ))}
      </div>

      {/* Event modal */}
      {openEvent && (
        <EventModal event={openEvent} currentUser={currentUser} lang={lang}
          onClose={() => { setOpenEvent(null); setShowQR(false); setShowAttendees(false); }}
          registered={regSet.has(openEvent.id)}
          attended={attSet.has(openEvent.id)}
          onRegister={handleRegister}
          onCancelRegistration={cancelEventRegistration}
          onShowQR={() => setShowQR(true)}
          onShowAttendees={() => setShowAttendees(true)}
          onShowRefund={() => setShowRefund(true)}/>
      )}

      {/* QR Ticket */}
      {showQR && openEvent && (
        <TicketSheet event={openEvent} currentUser={currentUser}
          attended={attSet.has(openEvent.id)}
          onClose={() => setShowQR(false)} tr={tr}/>
      )}

      {/* Attendees list */}
      {showAttendees && openEvent && (
        <AttendeesSheet event={openEvent} onClose={() => setShowAttendees(false)} tr={tr}/>
      )}

      {/* ── Stripe Checkout sheet ── */}
      {showCheckout && openEvent && (
        <div onClick={()=>setShowCheckout(false)} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', maxWidth:480, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)', maxHeight:'calc(92vh - var(--bottom-h,56px))', overflowY:'auto' }}>
            <StripeCheckout
              event={openEvent}
              onClose={() => setShowCheckout(false)}
              onSuccess={async (receipt) => {
                if (openEvent) {
                  await registerForEvent(openEvent.id, receipt.paymentId, receipt.amount);
                  setRegisteredEvents(prev => new Set([...prev, openEvent.id]));
                }
                setShowCheckout(false);
                setShowQR(true);
              }}
            />
          </div>
        </div>
      )}

      {/* ── Refund modal ── */}
      {showRefund && openEvent && (() => {
        const receipt = receipts.find(r => r.eventId === openEvent.id);
        return (
          <div onClick={()=>setShowRefund(false)} style={{ position:'fixed', inset:0, zIndex:900, background:'var(--overlay-modal)', backdropFilter:'blur(8px)', display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
            <div onClick={e=>e.stopPropagation()} style={{ background:'var(--black)', border:'1px solid var(--border)', maxWidth:480, width:'100%', borderRadius:'var(--r-xl) var(--r-xl) 0 0', marginBottom:'var(--bottom-h, 56px)' }}>
              <RefundModal
                event={openEvent}
                receipt={receipt}
                onClose={() => setShowRefund(false)}
                onRefunded={() => {
                  setReg(r => { const n = new Set(r); n.delete(openEvent.id); return n; });
                  setShowRefund(false);
                  setOpenEvent(null);
                }}
              />
            </div>
          </div>
        );
      })()}
    </div>
  );
}
